import { MODEL_ANIMATIONS, MODEL_COST, MODEL_FOOT_GAITS, MODEL_GAITS, MODEL_HEIGHT_LOOPS, MODEL_IDLE, MODEL_LIMITS, MODEL_ROUND_LOOPS, MODEL_TRAVEL, modelCatalogueOf, modelCost, } from './models.js';
import { checkWorldPackage } from './world-package/check.js';
import { MB, SKY_COST, WORLD_FIXED } from './world-package/cost.js';
import { CODE_ROUTE_SLACK, CODE_WORLD_ROUTES, LESSON_AREA, STOP_WORDS, SUBJECT_MIN, SUBJECT_OPTION, lessonAreaProblems, subjectClashes, subjectLookalikes, } from './world-package/design-rules.js';
import { BLOCK_CAPS, BLOCK_TYPES, WORLD_FORMAT_VERSIONS, WORLD_PACKAGE_BUDGET, WORLD_PACKAGE_LIMITS, WORLD_PACKAGE_PLACEMENT_BUDGET, } from './world-package/format.js';
import { NODE_SPACING_MAX, groundFitProblems } from './world-package/heights.js';
import { COVER_DIGITS, COVER_RADIUS, GROUND_FIT, OVERHEAD, SCENERY, SIGHT, SIGHT_BUDGET, } from './world-package/measure.js';
import { checkWorldFormat } from './world-package/validate.js';
/**
 * The Regelcheck (ADR-071; docs/superpowers/specs/2026-10-20-regelcheck-design.md): a published world
 * or library model judged against the rules an upload is held to today, with exactly the platform's
 * own checks, split into rules a repair task can name. Nothing here is a new rule: each finding is a
 * checker's problem, its message unchanged.
 *
 * Not in `world-package/`, which the kit copies: the kit judges a package as it is made, this judges
 * one that is already published.
 */
/**
 * Bumped whenever a rule the Regelcheck applies changes (a number, a word list, a check's code): the
 * API runs every published item again on its first start with a new version. regelcheck.test.ts
 * holds the rule constants to it by a hash; a change of a check's code needs the bump by hand.
 */
export const RULES_VERSION = 6;
export const REGEL_IDS = [
    'wereld-pakket',
    'wereld-grond',
    'wereld-lesgebied',
    'wereld-dubbel-onderwerp',
    'wereld-lijkt-op-model',
    'wereld-namen',
    'model-driehoeken',
    'model-tekenopdrachten',
    'model-textuurgeheugen',
    'model-bestand',
    'model-maat',
    'model-animaties',
    'model-feiten',
    'model-kosten',
    'model-texturen',
    'model-dichtbij-bouwbaar',
    'model-feitenblad',
];
/** A size in metres as a maker says it: 0.05 → "5 cm", 40 → "40 m". */
const sizeWords = (metres) => metres < 1
    ? `${(Math.round(metres * 1000) / 10).toLocaleString('nl-NL')} cm`
    : `${metres.toLocaleString('nl-NL')} m`;
/** The rules with their words built from `limits` (MODEL_LIMITS, unless a test says otherwise). */
export function regelcheckRulesFor(L) {
    return {
        'wereld-pakket': {
            soort: 'wereld',
            titel: 'Het pakket klopt',
            ernst: 'urgent',
            hoe: 'Bouw de wereld opnieuw met de nieuwste kit, laat `wereld check` hem goedkeuren en lever de zip aan in deze taak.',
            kit: 'REGELS.md, "Het podium", "Het budget" en "Plafonds"',
        },
        'wereld-grond': {
            soort: 'wereld',
            titel: 'Grond op de hoogtekaart',
            ernst: 'urgent',
            hoe: 'Laat de kit hoogte.bin uit de grond meten: kit.heights(spec, hoogtefunctie, { uitGrond: true }), of kit.heights(spec) als alle grond grond… heet. Controleer met `wereld check` en lever de zip aan in deze taak.',
            kit: 'REGELS.md, "Het podium"',
        },
        'wereld-lesgebied': {
            soort: 'wereld',
            titel: 'Achtergrond blijft achtergrond',
            ernst: 'urgent',
            hoe: `Leg elke route verder dan ${String(LESSON_AREA.radius + LESSON_AREA.margin).replace('.', ',')} m van het midden, of laat het bewegende deel staan. Gaat de wereld over wat er beweegt (de bloedstroom, de zaadcellen), maak dat onderdeel dan het onderwerp van de wereld: ${SUBJECT_OPTION}; zijn routes mogen dan in het lesgebied komen, niet in de vrije ruimte. Controleer met \`wereld check\` en lever de zip aan in deze taak.`,
            kit: 'REGELS.md, "Achtergrond en onderwerp"',
        },
        'wereld-dubbel-onderwerp': {
            soort: 'wereld',
            titel: 'Geen dubbel onderwerp',
            ernst: 'urgent',
            hoe: `Laat het bewegende deel staan of geef het een naam die niet op een model uit de bibliotheek lijkt; wat een les moet laten bewegen, wordt een model. Gaat de wereld over dit onderdeel zelf, maak het dan het onderwerp van de wereld: ${SUBJECT_OPTION} (dan waarschuwt het platform alleen). Lever de zip aan in deze taak.`,
            kit: 'REGELS.md, "Achtergrond en onderwerp"',
        },
        'wereld-lijkt-op-model': {
            soort: 'wereld',
            titel: 'Lijkt op een model',
            ernst: 'graag-verbeteren',
            hoe: 'Is het bewegende deel iets anders dan het model uit de bibliotheek, geef het (of zijn route en mesh) dan een eigen naam. Lever de zip aan in deze taak.',
            kit: 'REGELS.md, "Achtergrond en onderwerp"',
        },
        'wereld-namen': {
            soort: 'wereld',
            titel: 'Oude namen en volgorde',
            ernst: 'graag-verbeteren',
            hoe: 'Laat elke oude naam (eerder, eerdereOnderdelen) naar een huidig onderdeel of een huidige toestand wijzen; de volgorde zet het platform zelf bij het uploaden. Lever de zip aan in deze taak.',
            kit: 'REGELS.md, "Een nieuwe versie: lessen blijven werken"',
        },
        'model-driehoeken': {
            soort: 'model',
            titel: `Hooguit ${L.trianglesHardMax.toLocaleString('nl-NL')} driehoeken`,
            ernst: 'urgent',
            hoe: 'Maak het model eenvoudiger (minder driehoeken), controleer met `model check` en lever het GLB-bestand aan in deze taak.',
            kit: 'MODEL.md, "Tekenopdrachten en kosten"',
        },
        'model-tekenopdrachten': {
            soort: 'model',
            titel: `Hooguit ${L.drawCallsMax} tekenopdrachten`,
            ernst: 'urgent',
            hoe: 'Voeg delen met hetzelfde materiaal samen (model.samenvoegen()), controleer met `model check` en lever het GLB-bestand aan in deze taak.',
            kit: 'MODEL.md, "Minder tekenopdrachten: model.samenvoegen()"',
        },
        'model-textuurgeheugen': {
            soort: 'model',
            titel: 'Texturen passen in het geheugen',
            ernst: 'urgent',
            hoe: `Gebruik minder of kleinere texturen (hooguit ${L.textureSize} pixels), controleer met \`model check\` en lever het GLB-bestand aan in deze taak.`,
            kit: 'MODEL.md, "Tekenopdrachten en kosten"',
        },
        'model-bestand': {
            soort: 'model',
            titel: `Bestand hooguit ${(Math.round((L.fileBytesMax / 2 ** 20) * 10) / 10).toLocaleString('nl-NL')} MB`,
            ernst: 'urgent',
            hoe: 'Maak het bestand kleiner (minder driehoeken, kleinere texturen), controleer met `model check` en lever het GLB-bestand aan in deze taak.',
            kit: 'MODEL.md, "Wat model check zegt"',
        },
        'model-maat': {
            soort: 'model',
            titel: 'Echte grootte',
            ernst: 'urgent',
            hoe: `Geef het model een echte grootte tussen ${sizeWords(L.realSizeMin)} en ${sizeWords(L.realSizeMax)} en lever het opnieuw aan in deze taak.`,
            kit: 'MODEL.md, "Echte grootte"',
        },
        'model-animaties': {
            soort: 'model',
            titel: 'Bekende animatienamen',
            ernst: 'urgent',
            hoe: 'Geef elke clip een naam uit de lijst van het platform (animaties_benoemen) en lever het model opnieuw aan in deze taak.',
            kit: 'MODEL.md, "Clips: lus, snelheid en richting"',
        },
        'model-feiten': {
            soort: 'model',
            titel: 'De bril kan het model lezen',
            ernst: 'urgent',
            hoe: 'Lever het model opnieuw aan in deze taak; het platform meet het dan opnieuw.',
            kit: 'MODEL.md, "Wat de bril laat zien"',
        },
        'model-kosten': {
            soort: 'model',
            titel: 'Kosten volgens de huidige formule',
            ernst: 'graag-verbeteren',
            hoe: 'Een beheerder verwerkt het model opnieuw ("Opnieuw verwerken"); er hoeft niets aan het model te veranderen.',
            kit: 'MODEL.md, "Tekenopdrachten en kosten"',
        },
        'model-texturen': {
            soort: 'model',
            titel: 'Alle texturen komen mee',
            ernst: 'graag-verbeteren',
            hoe: `Gebruik hooguit ${L.texturesMax} texturen, als JPEG of PNG, controleer met \`model check\` en lever het GLB-bestand aan in deze taak.`,
            kit: 'MODEL.md, "Wat model check zegt"',
        },
        'model-dichtbij-bouwbaar': {
            soort: 'model',
            titel: 'Van dichtbij te bekijken',
            ernst: 'urgent',
            hoe: "Het platform kan van dit model geen les 'Eén ding van dichtbij' bouwen. Meld het aan het platform; het model wordt voor die soort les niet aangeboden tot het opgelost is.",
            kit: 'MODEL.md, "Wat model check zegt"',
        },
        'model-feitenblad': {
            soort: 'model',
            titel: 'Een goedgekeurd feitenblad',
            ernst: 'graag-verbeteren',
            hoe: 'Heeft het model geen feitenblad, of alleen een concept van het lokale model: schrijf het feitenblad met de modelkit (feitenblad.md: de onderdelen die het model echt heeft, per kant, korte teksten in het Nederlands, met bronnen) en lever het aan met feitenblad_uploaden in deze taak (met controleren: true zegt het eerst wat er niet klopt, zonder iets te bewaren); het wordt een concept. Wacht er een concept van een mens of uit de kit, dan leest een mens het na en keurt het goed in Inhoud → Feitenbladen. Het model blijft gepubliceerd.',
            kit: 'MODEL.md, "Het feitenblad"',
        },
    };
}
export const REGELCHECK_RULES = regelcheckRulesFor(MODEL_LIMITS);
const finding = (rule, message) => ({
    rule,
    severity: REGELCHECK_RULES[rule].ernst,
    message,
});
/**
 * A model the Dichtbij skeleton cannot be built for (`model-dichtbij-bouwbaar`): the failing check's label
 * and detail as the finding's message. Made in the API, which owns the skeleton.
 */
export const dichtbijFinding = (message) => finding('model-dichtbij-bouwbaar', message);
/**
 * A published model whose current fact sheet (its newest version that is not withdrawn, ADR-081) is not
 * approved (`model-feitenblad`, ADR-082). Made in the API, which owns the sheets.
 */
export const feitenbladFinding = (message) => finding('model-feitenblad', message);
/** The problems of one rule as one finding: their messages, each once, in order. */
function findingOf(rule, problems) {
    if (problems.length === 0)
        return [];
    const messages = [...new Set(problems.map((p) => p.message))];
    return [finding(rule, messages.join('\n'))];
}
/** A format problem about old names or the recorded order (ADR-070): the `wereld-namen` rule. */
const isNamesProblem = (problem) => problem.path === '/order' ||
    problem.path.startsWith('/order/') ||
    problem.path.startsWith('/eerdereOnderdelen') ||
    /^\/features\/[^/]+\/eerder(\/|$)/.test(problem.path);
/** A package without its old names and order: what the design rules can still read when only those fail. */
function withoutNames(json) {
    if (typeof json !== 'object' || json === null || Array.isArray(json))
        return json;
    const { order: _order, eerdereOnderdelen: _old, ...rest } = json;
    const features = rest['features'];
    if (typeof features !== 'object' || features === null || Array.isArray(features))
        return rest;
    return {
        ...rest,
        features: Object.fromEntries(Object.entries(features).map(([name, feature]) => {
            if (typeof feature !== 'object' || feature === null || Array.isArray(feature))
                return [name, feature];
            const { eerder: _eerder, ...kept } = feature;
            return [name, kept];
        })),
    };
}
/**
 * Whether a stored ground fit should be measured again before it is judged: measured before worlds
 * without a ground (no `depth`, 20 Oct 2026) and failing, so a surface far below (a world in space)
 * may have been taken for its ground. One that passes stays passed.
 */
export function groundFitStale(fit) {
    return (fit !== null &&
        fit !== undefined &&
        !fit.tooMany &&
        fit.depth === undefined &&
        groundFitProblems(fit).length > 0);
}
/** A published world against today's rules (spec, decision 1). Total: never throws. */
export function checkWorldItem(input) {
    const findings = [];
    const unmeasured = [];
    let measured;
    let stored;
    if (input.measured) {
        const { groundFit, ...rest } = input.measured;
        measured = rest;
        stored = groundFit;
    }
    // The package as an upload is judged, without the ground fit: that is a rule of its own.
    const heights = input.heights instanceof Uint8Array ? input.heights : undefined;
    const whole = checkWorldPackage({
        json: input.json,
        files: input.files,
        ...(heights ? { heights } : {}),
        ...(measured ? { measured } : {}),
        codeWorlds: input.codeWorlds,
    });
    findings.push(...findingOf('wereld-pakket', whole.problems.filter((p) => !isNamesProblem(p))), ...findingOf('wereld-namen', whole.problems.filter(isNamesProblem)));
    // From the package as stored, not the checked one: a package with any problem (old names only) has none.
    const rawHeights = input.json?.terrain
        ?.heights?.file;
    const heightsFile = typeof rawHeights === 'string' ? rawHeights : undefined;
    if (input.heights === 'missing' && heightsFile !== undefined) {
        findings.push(finding('wereld-pakket', `de hoogtekaart ${heightsFile} staat niet in de opslag: de bril kan de wereld niet bouwen`));
    }
    else if (!findings.some((f) => f.rule === 'wereld-pakket') &&
        (measured === undefined || (heightsFile !== undefined && heights === undefined))) {
        unmeasured.push('wereld-pakket');
    }
    const fit = input.groundFit ?? stored;
    if (fit === undefined || fit === null || fit.tooMany)
        unmeasured.push('wereld-grond');
    else
        findings.push(...findingOf('wereld-grond', groundFitProblems(fit)));
    // The design rules (ADR-069) read the package as the format reads it; when only its old names or
    // order fail, without them.
    const pkg = whole.package ??
        checkWorldFormat(input.json, input.files).package ??
        checkWorldFormat(withoutNames(input.json), input.files).package;
    if (pkg) {
        try {
            findings.push(...findingOf('wereld-lesgebied', lessonAreaProblems(pkg)));
            findings.push(...findingOf('wereld-dubbel-onderwerp', subjectClashes(pkg, input.library)));
            findings.push(...findingOf('wereld-lijkt-op-model', subjectLookalikes(pkg, input.library)));
        }
        catch {
            // A package the format let through but a rule cannot read is the package's problem.
            if (!findings.some((f) => f.rule === 'wereld-pakket'))
                findings.push(finding('wereld-pakket', 'het pakket heeft een vorm die niet te lezen is'));
        }
    }
    return { findings, unmeasured };
}
const nl = (n) => n.toLocaleString('nl-NL');
const mb = (bytes) => (Math.round((bytes / 2 ** 20) * 10) / 10).toLocaleString('nl-NL');
/** A published library model against today's limits (spec, decision 1). Total: never throws. */
export function checkModelItem(input) {
    const { facts } = input;
    const out = [];
    const L = MODEL_LIMITS;
    try {
        if (facts.triangles > L.trianglesHardMax)
            out.push(finding('model-driehoeken', `Het model heeft ${nl(facts.triangles)} driehoeken; hooguit ${nl(L.trianglesHardMax)}.`));
        if (facts.drawCalls > L.drawCallsMax)
            out.push(finding('model-tekenopdrachten', `Het model heeft ${facts.drawCalls} tekenopdrachten (draw calls); hooguit ${L.drawCallsMax}.`));
        if (facts.gpuBytes > L.gpuBytesMax)
            out.push(finding('model-textuurgeheugen', `De texturen nemen ${mb(facts.gpuBytes)} MB geheugen; hooguit ${mb(L.gpuBytesMax)} MB.`));
        if (input.bytes > L.fileBytesMax)
            out.push(finding('model-bestand', `Het bestand is ${mb(input.bytes)} MB; hooguit ${mb(L.fileBytesMax)} MB.`));
        if (facts.realSize < L.realSizeMin || facts.realSize > L.realSizeMax)
            out.push(finding('model-maat', `Het model is ${String(facts.realSize).replace('.', ',')} m groot; tussen ${String(L.realSizeMin).replace('.', ',')} en ${L.realSizeMax} m.`));
        const names = Object.keys(facts.animations);
        const unknown = names.filter((name) => !MODEL_ANIMATIONS.includes(name));
        if (unknown.length > 0)
            out.push(finding('model-animaties', `${unknown.map((name) => `"${name}"`).join(', ')} ${unknown.length === 1 ? 'is geen animatienaam' : 'zijn geen animatienamen'} van het platform (wel: ${MODEL_ANIMATIONS.join(', ')}).`));
        if (!Object.hasOwn(modelCatalogueOf({ [facts.slug]: facts }), facts.slug))
            out.push(finding('model-feiten', names.length > 0 && !names.includes(MODEL_IDLE)
                ? `Het model beweegt maar heeft geen "${MODEL_IDLE}"; de bril laat het daarom weg uit een les.`
                : 'De bril kan de gemeten gegevens van het model niet lezen en laat het weg uit een les.'));
        const cost = modelCost(facts);
        if (cost !== facts.cost)
            out.push(finding('model-kosten', `De kosten zijn ${facts.cost} van de 100; volgens de huidige formule ${cost}.`));
        const dropped = input.warnings
            .filter((w) => /^textures-dropped:\d+$|^textures-unreadable:\d+$|^fbx-textures-dropped$/.test(w))
            .map((w) => {
            const [what, count] = w.split(':');
            if (what === 'textures-dropped')
                return `${count} textuur(en) weggelaten: meer dan ${L.texturesMax}.`;
            if (what === 'textures-unreadable')
                return `${count} textuur(en) konden niet gelezen worden.`;
            return 'De texturen van het FBX-bestand zijn weggelaten.';
        });
        if (dropped.length > 0)
            out.push(finding('model-texturen', dropped.join(' ')));
    }
    catch {
        out.push(finding('model-feiten', 'De bril kan de gemeten gegevens van het model niet lezen.'));
    }
    return out;
}
/**
 * Every constant the checks read, for the RULES_VERSION test: a change here changes the hash and must
 * come with a new RULES_VERSION.
 */
export function regelcheckRuleConstants() {
    return {
        rules: REGELCHECK_RULES,
        world: {
            WORLD_FORMAT_VERSIONS,
            WORLD_PACKAGE_LIMITS,
            WORLD_PACKAGE_BUDGET,
            WORLD_PACKAGE_PLACEMENT_BUDGET,
            BLOCK_CAPS,
            BLOCK_TYPES,
            SKY_COST,
            WORLD_FIXED,
            MB,
            NODE_SPACING_MAX,
            GROUND_FIT,
            OVERHEAD,
            COVER_RADIUS,
            COVER_DIGITS,
            SIGHT,
            SIGHT_BUDGET,
            SCENERY,
            LESSON_AREA,
            CODE_WORLD_ROUTES,
            CODE_ROUTE_SLACK,
            STOP_WORDS: [...STOP_WORDS].sort(),
            SUBJECT_MIN,
        },
        model: {
            MODEL_LIMITS,
            MODEL_COST,
            MODEL_ANIMATIONS,
            MODEL_IDLE,
            MODEL_GAITS,
            MODEL_FOOT_GAITS,
            MODEL_HEIGHT_LOOPS,
            MODEL_ROUND_LOOPS,
            MODEL_TRAVEL,
        },
    };
}
//# sourceMappingURL=regelcheck.js.map